import { useCallback, useEffect, useRef, useState } from 'react';
import { getJobCustomParameters } from '@apis/jobCustomParameters';
import { getRelease, type ModelReleaseDetail } from '@apis/model';
import {
  modelReleasePreviewError, type CustomParameterDefinitions,
} from '@/utils/jobCustomParameters';

export interface CustomParameterDefinitionsState {
  definitions: CustomParameterDefinitions;
  loading: boolean;
  error: boolean;
  /** 目录拉取失败后的重试入口；与加载同一次 revision 递增。 */
  reload: () => void;
  /** 读一个模型主版本的详情（含 manifest 预览），同一个版本在本轮内只请求一次。 */
  loadRelease: (name: string) => Promise<ModelReleaseDetail>;
}

/**
 * 自定义参数目录 + 模型版本详情缓存的**唯一真源**。
 *
 * 顶层「模型主版本」字段与「高级选项 · 自定义参数」块共用这一份状态，理由是判断口径必须
 * 一致：两处各自拉一次目录，就可能一边显示"暂不可用"、另一边正常，重试按钮也只能刷新一处。
 *
 * 调用方传 ``active={open}``：弹窗是同一组件实例 + ``destroyOnHidden``，关闭期间拉取没有意义。
 * 与原先 `CustomParametersFields` 内部实现保持同款语义：失败时**清空**目录而不是留旧值，
 * 否则会拿上一次打开的目录去判断本轮参数的适用性。
 */
export function useCustomParameterDefinitions(active: boolean): CustomParameterDefinitionsState {
  const [definitions, setDefinitions] = useState<CustomParameterDefinitions>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [revision, setRevision] = useState(0);
  // 本轮内的模型版本详情请求去重：拒绝后允许重试，成功后整个弹窗会话内复用。
  const releaseRequests = useRef(new Map<string, Promise<ModelReleaseDetail>>());

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    releaseRequests.current.clear();
    setLoading(true);
    setError(false);
    getJobCustomParameters().then(value => {
      if (!cancelled) setDefinitions(value);
    }).catch(() => {
      if (!cancelled) { setError(true); setDefinitions({}); }
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [active, revision]);

  const loadRelease = useCallback((name: string): Promise<ModelReleaseDetail> => {
    const existing = releaseRequests.current.get(name);
    if (existing) return existing;
    const pending = getRelease(name).then(detail => {
      const error = modelReleasePreviewError(detail, name);
      if (!detail || error) throw new Error(error ?? '模型版本不存在');
      return detail;
    }).catch(err => {
      if (releaseRequests.current.get(name) === pending) releaseRequests.current.delete(name);
      throw err;
    });
    releaseRequests.current.set(name, pending);
    return pending;
  }, []);

  const reload = useCallback(() => setRevision(value => value + 1), []);

  return { definitions, loading, error, reload, loadRelease };
}
